import { useState, useContext } from 'react'
import { IntakeContext } from 'context/IntakeContext'
import dashboardData from 'layouts/authenticated/pages/dashboard/data/dashboardData'
import { selectData } from 'layouts/onboarding'
import {
  format,
  addYears,
  addDays,
  addMonths,
  max,
  min,
  differenceInMonths,
  subMonths,
} from 'date-fns'
import scoreData from '../data/scoreData'

export const usePARCOScoreCalculations = (
  assets = [],
  liabilities = [],
  adjustedWorkingNetTakeHome = 0,
  adjustedNetTakeHomeInRetirement = 0,
  adjustedNetTakeHomeDifferencePARCOGap = 0
) => {
  const { intakeState } = useContext(IntakeContext)
  const estimatedTaxRate = intakeState?.taxRate
    ? intakeState.taxRate / 100
    : 0.2
  const payoutType =
    intakeState?.familyMaritalStatus === 'married' ? 'Joint' : 'Single'
  const inflationConcern =
    intakeState?.inflation === 'concernedInflation' ? true : false
  try {
    // Calcs for FandG Annuity Cost
    const fngSafeIncomeAdvantageCost = ({
      dateOfBirth = new Date(),
      plannedRetirementDate = new Date(),
      grossIncomeGap = 0,
    }) => {
      const roundedCurrentAge = Math.min(
        Math.max(
          Math.round(
            differenceInMonths(new Date(), new Date(dateOfBirth)) / 12
          ),
          50
        ),
        80
      )
      const deferralYears = Math.min(
        Math.floor(
          differenceInMonths(new Date(plannedRetirementDate), new Date()) / 12
        ),
        20
      )
      const payoutRatio =
        scoreData.fngSafeIncomeAdvantage[roundedCurrentAge][deferralYears]
      const cost = (grossIncomeGap * 12) / payoutRatio
      return {
        roundedCurrentAge,
        deferralYears,
        payoutRatio,
        grossIncomeGap,
        cost,
      }
    }

    const fngReference = fngSafeIncomeAdvantageCost({
      dateOfBirth: intakeState?.dateOfBirth,
      plannedRetirementDate: intakeState?.dateOfPlannedRetirement,
      grossIncomeGap: adjustedNetTakeHomeDifferencePARCOGap,
    })

    // Calcs for NorthAmerican Annuity Cost
    const northAmericanBenefitSolutionsCost = ({
      dateOfBirth = new Date(),
      plannedRetirementDate = new Date(),
      grossIncomeGap = 0,
    }) => {
      const roundedCurrentAge = Math.min(
        Math.max(
          Math.round(
            differenceInMonths(new Date(), new Date(dateOfBirth)) / 12
          ),
          40
        ),
        79
      )
      const deferralYears = Math.min(
        Math.floor(
          differenceInMonths(new Date(plannedRetirementDate), new Date()) / 12
        ),
        15
      )
      const payoutRatio =
        scoreData.northAmericanBenefitSolutions[roundedCurrentAge][
          deferralYears
        ]
      const cost = (grossIncomeGap * 12) / payoutRatio
      return {
        roundedCurrentAge,
        deferralYears,
        payoutRatio,
        grossIncomeGap,
        cost,
      }
    }

    const naReference = northAmericanBenefitSolutionsCost({
      dateOfBirth: intakeState?.dateOfBirth,
      plannedRetirementDate: intakeState?.dateOfPlannedRetirement,
      grossIncomeGap: adjustedNetTakeHomeDifferencePARCOGap,
    })

    const parcoAnnuityRecommendationCalculator = ({
      dateOfBirth = new Date(),
      plannedRetirementDate = new Date(),
      netIncomeGap = 0,
    }) => {
      const estimatedTaxRate = intakeState?.taxRate
        ? intakeState.taxRate / 100
        : 0.2
      const payoutType =
        intakeState?.familyMaritalStatus === 'married' ? 'joint' : 'single'
      const inflationConcern =
        intakeState?.inflation === 'concernedInflation' ? 'Inflation' : 'Level'
      const payoutTable = `${payoutType}${inflationConcern}`
      const roundedCurrentAge = Math.min(
        Math.max(
          Math.round(
            differenceInMonths(new Date(), new Date(dateOfBirth)) / 12
          ),
          50
        ),
        100
      )
      // adjust to make sure the min is at least 0
      const deferralYears = Math.max(
        0,
        Math.min(
          Math.floor(
            differenceInMonths(new Date(plannedRetirementDate), new Date()) / 12
          ),
          25
        )
      )
      const payoutRatio =
        scoreData[payoutTable][roundedCurrentAge][deferralYears]
      const grossIncomeGap = (netIncomeGap / (1 - estimatedTaxRate)) * 12
      const cost = grossIncomeGap / payoutRatio
      const threePercent = grossIncomeGap / 0.03
      const fourPercent = grossIncomeGap / 0.04
      return {
        roundedCurrentAge,
        deferralYears,
        netIncomeGap,
        grossIncomeGap,
        payoutType,
        inflationConcern,
        payoutTable,
        payoutRatio,
        cost,
        threePercent,
        fourPercent,
      }
    }

    const hasThriftSavingsPlan = assets?.some(
      (item) =>
        item.externalAsset === true &&
        item.externalInstitutionName === 'Thrift Savings Plan'
    )

    const valueOfAssets =
      (hasThriftSavingsPlan
        ? 0
        : (intakeState?.tspBalance || 0) + (intakeState?.tspRothBalance || 0)) +
      (assets || [])
        .reduce((filtered, option) => {
          if (
            option.subType === 'checkingsavings' ||
            option.subType === 'investment'
          ) {
            var newList = {
              name: option.name,
              amount: option.amount,
            }
            filtered.push(newList)
          }
          return filtered
        }, [])
        .map((assets) => assets.amount)
        .reduce((a, b) => a + b, 0) -
      (liabilities || [])
        .reduce((filtered, option) => {
          if (option.subType === 'creditcard') {
            var newList = {
              name: option.name,
              amount: option.amount,
            }
            filtered.push(newList)
          }
          return filtered
        }, [])
        .map((liabilities) => liabilities.amount)
        .reduce((a, b) => a + b, 0)

    const valueOfInvestableAssets =
      (hasThriftSavingsPlan
        ? 0
        : (intakeState?.tspBalance || 0) + (intakeState?.tspRothBalance || 0)) +
      (assets || [])
        .reduce((filtered, option) => {
          if (
            option.subType === 'checkingsavings' ||
            option.subType === 'investment'
          ) {
            var newList = {
              name: option.name,
              amount: option.amount,
            }
            filtered.push(newList)
          }
          return filtered
        }, [])
        .map((assets) => assets.amount)
        .reduce((a, b) => a + b, 0)

    const parcoLifestyleGoal =
      intakeState?.retirementLifestyleGoal?.lifestyleGoal || 1
    // adjust for determined goal
    const parcoReadinessScoreLifestyleGoal = Math.max(
      0,
      (adjustedNetTakeHomeInRetirement * 12 +
        valueOfAssets *
          0.75 *
          0.04 *
          (1 - (intakeState?.taxRate || 20) / 100)) /
        (adjustedWorkingNetTakeHome * parcoLifestyleGoal * 12) ===
        Infinity
        ? 0
        : Math.min(
            (adjustedNetTakeHomeInRetirement * 12 +
              valueOfAssets *
                0.75 *
                0.04 *
                (1 - (intakeState?.taxRate || 20) / 100)) /
              (adjustedWorkingNetTakeHome * parcoLifestyleGoal * 12),
            0.95
          )
    )
    const parcoReadinessScore4Percent = Math.max(
      0,
      (adjustedNetTakeHomeInRetirement * 12 +
        valueOfAssets *
          0.75 *
          0.04 *
          (1 - (intakeState?.taxRate || 20) / 100)) /
        (adjustedWorkingNetTakeHome * parcoLifestyleGoal * 12) ===
        Infinity
        ? 0
        : Math.min(
            (adjustedNetTakeHomeInRetirement * 12 +
              valueOfAssets *
                0.75 *
                0.04 *
                (1 - (intakeState?.taxRate || 20) / 100)) /
              (adjustedWorkingNetTakeHome * parcoLifestyleGoal * 12),
            0.95
          )
    )

    const parcoReadinessScore3Percent = Math.max(
      0,
      (adjustedNetTakeHomeInRetirement * 12 +
        valueOfAssets *
          0.75 *
          0.03 *
          (1 - (intakeState?.taxRate || 20) / 100)) /
        (adjustedWorkingNetTakeHome * parcoLifestyleGoal * 12) ===
        Infinity
        ? 0
        : Math.min(
            (adjustedNetTakeHomeInRetirement * 12 +
              valueOfAssets *
                0.75 *
                0.03 *
                (1 - (intakeState?.taxRate || 20) / 100)) /
              (adjustedWorkingNetTakeHome * parcoLifestyleGoal * 12),
            0.95
          )
    )
    // adjust parco gap for lifestyle goal
    const parcoGapAdjustedForLifestyleGoal =
      adjustedWorkingNetTakeHome * parcoLifestyleGoal -
      adjustedNetTakeHomeInRetirement

    const parcoAnnuityRecommendation = parcoAnnuityRecommendationCalculator({
      dateOfBirth: intakeState?.dateOfBirth,
      plannedRetirementDate: intakeState?.dateOfPlannedRetirement,
      netIncomeGap: parcoGapAdjustedForLifestyleGoal,
    })

    const parcoReadinessScoreLevelAnnuity = 0
    const parcoReadinessScoreInflationAdjustedAnnuity = 0

    const parco3PercentRuleCost =
      ((adjustedNetTakeHomeDifferencePARCOGap / (1 - estimatedTaxRate)) * 12) /
      0.03
    const parco4PercentRuleCost =
      ((adjustedNetTakeHomeDifferencePARCOGap / (1 - estimatedTaxRate)) * 12) /
      0.04
    const parcoAnnuityCost = parcoAnnuityRecommendation.cost

    // console.log(
    //   'parcoAnnuityRecommendationFromCalculator:',
    //   parcoAnnuityRecommendation
    // )
    // console.log('fng:', fngReference)

    return {
      northAmericanBenefitSolutionsCost,
      fngSafeIncomeAdvantageCost,
      parcoAnnuityRecommendationCalculator,
      parcoAnnuityRecommendation,
      parcoReadinessScoreLifestyleGoal,
      parcoReadinessScore4Percent,
      parcoReadinessScore3Percent,
      parco3PercentRuleCost,
      parco4PercentRuleCost,
      parcoAnnuityCost,
    }
  } catch (e) {
    console.log(e)
    return {}
  }
}
